import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { applyActionCode } from 'firebase/auth';
import { auth } from '../firebase';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

type Status = 'loading' | 'success' | 'error';

const VerifyActionPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState<Status>('loading');
    const [errorDetail, setErrorDetail] = useState<string>('');
    const hasRun = useRef(false);

    useEffect(() => {
        // Guard against React StrictMode double-invocation consuming the oobCode twice
        if (hasRun.current) return;
        hasRun.current = true;

        const oobCode = searchParams.get('oobCode');
        const mode = searchParams.get('mode');

        console.log('[VerifyAction] mode:', mode, 'oobCode:', oobCode ? `${oobCode.slice(0, 8)}...` : 'missing');

        if (!oobCode) {
            setErrorDetail('Chybí ověřovací kód v odkazu.');
            setStatus('error');
            return;
        }

        applyActionCode(auth, oobCode)
            .then(async () => {
                console.log('[VerifyAction] Email verified successfully');
                // Refresh the local user so emailVerified updates in AuthContext
                if (auth.currentUser) {
                    await auth.currentUser.reload();
                    await auth.currentUser.getIdToken(true);
                }
                setStatus('success');
                setTimeout(() => {
                    // Force full page reload to ensure AuthContext picks up the verified state
                    window.location.href = '/';
                }, 3000);
            })
            .catch((err) => {
                console.error('[VerifyAction] Failed:', err.code, err.message);
                if (err.code === 'auth/invalid-action-code') {
                    setErrorDetail('Odkaz již byl použit nebo vypršel. Zkuste si nechat poslat nový e-mail.');
                } else if (err.code === 'auth/expired-action-code') {
                    setErrorDetail('Platnost odkazu vypršela. Zkuste si nechat poslat nový e-mail.');
                } else {
                    setErrorDetail(err.message || 'Neznámá chyba.');
                }
                setStatus('error');
            });
    }, [searchParams, navigate]);

    return (
        <div className="min-h-screen bg-[#131f24] flex items-center justify-center p-4">
            <div className="w-full max-w-md bg-[#1a2c35] rounded-2xl shadow-2xl p-8 text-center">
                {status === 'loading' && (
                    <>
                        <Loader2 className="w-16 h-16 text-[#1cb0f6] animate-spin mx-auto mb-6" />
                        <h1 className="text-2xl font-bold text-white mb-2">Ověřuji e-mail...</h1>
                        <p className="text-gray-400">Počkejte prosím.</p>
                    </>
                )}

                {status === 'success' && (
                    <>
                        <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                            <CheckCircle className="w-10 h-10 text-green-400" />
                        </div>
                        <h1 className="text-2xl font-bold text-white mb-2">E-mail ověřen!</h1>
                        <p className="text-gray-400 mb-6">Váš účet je nyní aktivní. Budete přesměrováni...</p>
                        <button
                            onClick={() => { window.location.href = '/'; }}
                            className="w-full bg-[#1cb0f6] hover:bg-[#1cb0f6]/90 text-white font-bold rounded-xl px-8 py-4 shadow-[0_4px_0_#1899d6] active:shadow-none active:translate-y-[4px] transition-all duration-150"
                        >
                            Přejít do aplikace
                        </button>
                    </>
                )}

                {status === 'error' && (
                    <>
                        <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                            <XCircle className="w-10 h-10 text-red-400" />
                        </div>
                        <h1 className="text-2xl font-bold text-white mb-2">Ověření se nezdařilo</h1>
                        <p className="text-gray-400 mb-6">{errorDetail || 'Odkaz mohl vypršet nebo již byl použit.'}</p>
                        <button
                            onClick={() => navigate('/', { replace: true })}
                            className="w-full bg-[#1cb0f6] hover:bg-[#1cb0f6]/90 text-white font-bold rounded-xl px-8 py-4 shadow-[0_4px_0_#1899d6] active:shadow-none active:translate-y-[4px] transition-all duration-150"
                        >
                            Zpět na hlavní stránku
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

export default VerifyActionPage;
